import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight, Loader2, CheckCircle2 } from "lucide-react";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { FactSection } from "@/components/factfind/FactSection";
import { STEPS, UNKNOWN, buildSummary, hasPartner } from "@/data/factFind";
import { StatementPicker } from "@/components/StatementPicker";
import { uploadFiles } from "@/lib/uploads";

const STATEMENTS_STEP = STEPS.findIndex((s) => s.title === "Super & insurance");

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STORE = "nua-fact-find-v1";

const load = () => {
  try {
    return JSON.parse(localStorage.getItem(STORE)) || { answers: {}, step: 0 };
  } catch {
    return { answers: {}, step: 0 };
  }
};

const known = (v) => (v && v !== UNKNOWN ? v : "");
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

const toTop = () => (window.__lenis ? window.__lenis.scrollTo(0, { immediate: true }) : window.scrollTo(0, 0));

const useNoIndex = () => {
  useEffect(() => {
    const prev = document.title;
    document.title = "Client Fact Find | NUA Private Wealth";
    const meta = Object.assign(document.createElement("meta"), { name: "robots", content: "noindex, nofollow" });
    document.head.appendChild(meta);
    return () => {
      document.title = prev;
      meta.remove();
    };
  }, []);
};

const Progress = ({ step }) => (
  <div data-testid="ff-progress" className="mt-10">
    <p className="text-[11px] uppercase tracking-[0.2em] text-[#A78558]">Step {step + 1} of {STEPS.length}</p>
    <div className="mt-3 grid gap-1.5" style={{ gridTemplateColumns: `repeat(${STEPS.length}, 1fr)` }}>
      {STEPS.map((s, i) => (
        <div key={s.title} className={`h-1 transition-colors duration-500 ${i <= step ? "bg-[#0A2333]" : "bg-black/10"}`} />
      ))}
    </div>
    <ol className="mt-3 hidden md:grid gap-1.5 text-[10px] uppercase tracking-[0.12em] text-[#8A8F8B]" style={{ gridTemplateColumns: `repeat(${STEPS.length}, 1fr)` }}>
      {STEPS.map((s, i) => (
        <li key={s.title} className={i === step ? "text-[#0A2333]" : ""}>{s.title}</li>
      ))}
    </ol>
  </div>
);

const Done = ({ onReset }) => (
  <div data-testid="ff-success" className="mt-12 border border-black/10 bg-white p-8 md:p-12">
    <CheckCircle2 size={30} className="text-[#A78558]" />
    <h2 className="mt-5 font-serif text-3xl tracking-tight text-[#111413]">Thank you — your fact find has been sent.</h2>
    <p className="mt-4 text-base leading-relaxed text-[#4A524D] max-w-xl">
      Kevin will review your details before your next conversation and will be in touch if anything needs clarifying.
    </p>
    <button type="button" data-testid="ff-start-new" onClick={onReset} className="mt-8 text-xs uppercase tracking-[0.15em] text-[#111413] underline decoration-[#A78558] decoration-2 underline-offset-8">
      Start a new fact find
    </button>
  </div>
);

export default function FactFindPage() {
  useNoIndex();
  const [{ answers, step, token }, setState] = useState(load);
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);
  const [website, setWebsite] = useState("");
  const [files, setFiles] = useState([]);
  const [progress, setProgress] = useState(null);
  const partner = hasPartner(answers);
  const draftEmail = known(answers["email.client"]);

  useEffect(() => {
    const t = setTimeout(() => localStorage.setItem(STORE, JSON.stringify({ answers, step, token })), 300);
    return () => clearTimeout(t);
  }, [answers, step, token]);

  useEffect(() => {
    let resume = new URLSearchParams(window.location.search).get("resume");
    try {
      resume = resume || sessionStorage.getItem("nua-resume");
      sessionStorage.removeItem("nua-resume");
    } catch {
      /* storage unavailable */
    }
    if (!resume) return;
    window.history.replaceState(null, "", "/fact-find");
    axios
      .get(`${API}/fact-find-drafts/${encodeURIComponent(resume)}`)
      .then(({ data }) => {
        setState({ answers: data.answers || {}, step: data.step || 0, token: resume });
        toast.success("Welcome back — your answers have been restored.");
      })
      .catch(() => toast.error("That link has expired or the fact find was already sent."));
  }, []);

  useEffect(() => {
    if (done || sending || !EMAIL_RE.test(draftEmail)) return undefined;
    const t = setTimeout(() => {
      const client_name = [known(answers["first_name.client"]), known(answers["surname.client"])].filter(Boolean).join(" ");
      axios
        .post(`${API}/fact-find-drafts`, { token, email: draftEmail, client_name, step, answers })
        .then(({ data }) => data.token !== token && setState((s) => ({ ...s, token: data.token })))
        .catch(() => {});
    }, 2500);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [answers, step, draftEmail, done, sending]);

  const set = (k, v) => setState((s) => ({ ...s, answers: { ...s.answers, [k]: v } }));
  const go = (n) => {
    setState((s) => ({ ...s, step: n }));
    toTop();
  };
  const reset = () => {
    localStorage.removeItem(STORE);
    setState({ answers: {}, step: 0, token: null });
    setDone(false);
    toTop();
  };

  const submit = async () => {
    setSending(true);
    const name = [known(answers["first_name.client"]), known(answers["surname.client"])].filter(Boolean).join(" ");
    try {
      const { data } = await axios.post(`${API}/fact-finds`, {
        client_name: name || "Not provided",
        email: known(answers["email.client"]) || null,
        phone: known(answers["mobile.client"]) || null,
        has_partner: partner,
        summary: buildSummary(answers),
        answers,
        file_count: files.length,
        draft_token: token || null,
        website,
      });
      if (data.upload_token) {
        const { failed } = await uploadFiles("fact-finds", data.id, data.upload_token, files, setProgress);
        if (failed) toast.error(`${failed} statement${failed > 1 ? "s" : ""} couldn't be uploaded — you can email them to hello@nuaprivatewealth.com.au.`);
      }
      setFiles([]);
      localStorage.removeItem(STORE);
      setState({ answers: {}, step: 0, token: null });
      setDone(true);
      toTop();
    } catch {
      toast.error("Your fact find couldn't be sent. Your answers are saved — please try again in a moment.");
    } finally {
      setSending(false);
      setProgress(null);
    }
  };

  const current = STEPS[step];
  const last = step === STEPS.length - 1;

  return (
    <>
      <Header />
      <main className="bg-[#F9F8F4] pt-16 md:pt-[88px]" data-testid="fact-find-page">
        <div className="mx-auto max-w-4xl px-6 md:px-10 py-12 md:py-20">
          <div className="flex items-center gap-5">
            <img src="/assets/nua-logo-transparent.png" alt="NUA Private Wealth" className="h-12 md:h-14 w-auto" data-testid="ff-logo" />
            <span className="h-10 w-px bg-black/15" aria-hidden="true" />
            <img src="/assets/nua-mark-transparent.png" alt="" aria-hidden="true" className="h-9 md:h-10 w-auto opacity-90" data-testid="ff-triskelion" />
          </div>
          <h1 data-testid="ff-heading" className="mt-10 font-serif text-4xl md:text-5xl tracking-tight text-[#111413]">Client Fact Find</h1>
          <p className="mt-4 text-base leading-relaxed text-[#4A524D] max-w-2xl">
            Nothing here is compulsory. Fill in what you know, tap <span className="text-[#A78558]">Unknown</span> if you're
            not sure, or leave it blank. Your answers are saved on this device as you go.
          </p>
          <p className="mt-3 text-sm leading-relaxed text-[#8A8F8B] max-w-2xl" data-testid="ff-draft-note">
            Once you add your email, your progress is also kept securely by NUA so you can finish on any device.
            If you don't send it, we'll email you a gentle reminder every few days (you can stop these at any time).
          </p>

          {done ? (
            <Done onReset={reset} />
          ) : (
            <>
              <Progress step={step} />
              <div className="mt-12">
                <h2 data-testid="ff-step-title" className="text-xs uppercase tracking-[0.25em] text-[#0A2333]">{current.title}</h2>
                <p className="mt-2 text-sm text-[#4A524D]">{current.intro}</p>
              </div>
              <div className="mt-8 space-y-12">
                {current.sections.map((s) => (
                  <FactSection key={s.title} section={s} answers={answers} set={set} partner={partner} />
                ))}
                {step === STATEMENTS_STEP && (
                  <section className="border-t border-black/10 pt-8" data-testid="ff-statements-section">
                    <h2 className="font-serif text-2xl md:text-3xl tracking-tight text-[#111413]">Statements</h2>
                    <div className="mt-6">
                      <StatementPicker
                        tone="light"
                        files={files}
                        onChange={setFiles}
                        onError={toast.error}
                        progress={progress}
                        label="Super & insurance statements (optional)"
                        hint="Snap a photo or attach your latest super or insurance statements"
                        testid="ff"
                      />
                    </div>
                    <p className="mt-3 text-xs text-[#8A8F8B]">Attached files are sent with your fact find but aren't saved on this device.</p>
                  </section>
                )}
              </div>
              <div aria-hidden="true" className="absolute left-[-9999px] h-0 w-0 overflow-hidden">
                <input tabIndex={-1} autoComplete="off" value={website} onChange={(e) => setWebsite(e.target.value)} />
              </div>
              <div className="mt-14 flex flex-wrap items-center justify-between gap-4 border-t border-black/10 pt-8">
                {step > 0 ? (
                  <button type="button" data-testid="ff-back" onClick={() => go(step - 1)} className="inline-flex items-center gap-2 px-2 py-3 text-xs uppercase tracking-[0.15em] text-[#111413]">
                    <ArrowLeft size={15} /> Back
                  </button>
                ) : (
                  <button type="button" data-testid="ff-clear" onClick={() => window.confirm("Clear all answers on this device?") && reset()} className="px-2 py-3 text-xs uppercase tracking-[0.15em] text-[#8A8F8B] hover:text-[#111413]">
                    Clear form
                  </button>
                )}
                <button
                  type="button"
                  data-testid={last ? "ff-submit" : "ff-next"}
                  disabled={sending}
                  onClick={() => (last ? submit() : go(step + 1))}
                  className="inline-flex items-center gap-2 rounded-full bg-[#0A2333] px-8 py-4 text-xs uppercase tracking-[0.15em] text-[#F9F8F4] transition-[background-color,transform,opacity] duration-300 hover:bg-[#071824] hover:-translate-y-0.5 disabled:opacity-60"
                >
                  {last ? (sending ? (progress ? "Uploading" : "Sending") : "Submit fact find") : `Next: ${STEPS[step + 1].title}`}
                  {sending ? <Loader2 size={15} className="animate-spin" /> : <ArrowRight size={15} />}
                </button>
              </div>
            </>
          )}
        </div>
      </main>
      <Footer />
    </>
  );
}
